import { NavLink, useLocation } from "react-router-dom";
import {
    Shield,
    Users,
    Building2,
    ScrollText,
    Activity,
    Database,
    Layers,
    LayoutDashboard,
    ShieldCheck,
    Mail,
    Megaphone,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_TABS, adminTabLocked, resolveAdminTab } from "@/lib/adminNav";

const TAB_ICONS = {
    dashboard: LayoutDashboard,
    users: Users,
    orgs: Building2,
    pools: Layers,
    traffic: Activity,
    emails: Mail,
    site: Megaphone,
    database: Database,
    audit: ScrollText,
    security: ShieldCheck,
};

function AdminTwoFactorBanner({ needs2fa }) {
    if (!needs2fa) return null;

    return (
        <div
            className="rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-100 lg:px-4 lg:py-3"
            data-testid="admin-2fa-required-banner"
        >
            <span className="lg:hidden">2FA required before using the console. </span>
            <span className="hidden lg:inline">
                Two-factor authentication is required before you can use the admin console. Complete setup in{" "}
            </span>
            <NavLink to="/admin/security" className="font-medium underline underline-offset-2 hover:text-white">
                Security
            </NavLink>
            <span className="hidden lg:inline">.</span>
        </div>
    );
}

/** Admin sub-header — desktop hero + tabs; mobile uses compact strip (nav is bottom bar). */
export function AdminConsoleChrome() {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const needs2fa = !user?.totp_enabled;
    const current = resolveAdminTab(pathname);

    return (
        <div className="admin-console-chrome shrink-0" data-testid="admin-console-chrome">
            <AdminTwoFactorBanner needs2fa={needs2fa} />

            {/* Mobile: compact current-section header */}
            <div className="admin-console-chrome__mobile lg:hidden">
                <div className="admin-console-chrome__mobile-head">
                    <p className="label-eyebrow inline-flex items-center gap-1.5">
                        <Shield className="admin-accent-icon h-3 w-3" /> Platform Admin
                    </p>
                    <h1 className="admin-console-chrome__mobile-title">{current.label}</h1>
                </div>
            </div>

            {/* Desktop: full hero + horizontal tabs */}
            <div className="admin-console-chrome__desktop hidden lg:block space-y-6 border-b border-white/[0.06] pb-6">
                <div>
                    <p className="label-eyebrow inline-flex items-center gap-1.5">
                        <Shield className="admin-accent-icon h-3.5 w-3.5" /> Platform Admin
                    </p>
                    <h1 className="mt-1 font-heading text-3xl tracking-tight xl:text-4xl">Super Admin Console</h1>
                    <p className="mt-1.5 max-w-2xl text-white/50">
                        Platform-wide users, organizations, traffic, database tools, and audit management. All actions are logged and enforced server-side.
                    </p>
                </div>

                <nav className="flex min-h-[42px] flex-wrap gap-1" aria-label="Admin sections">
                    {ADMIN_TABS.map((tab) => {
                        const Icon = TAB_ICONS[tab.key];
                        const locked = adminTabLocked(needs2fa, tab.key);
                        return (
                            <NavLink
                                key={tab.to}
                                to={tab.to}
                                className={({ isActive }) =>
                                    `admin-nav-link -mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                                        isActive ? "admin-nav-link--active" : "border-transparent text-white/55 hover:text-white"
                                    }${locked ? " pointer-events-none opacity-40" : ""}`
                                }
                                data-testid={`admin-nav-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <Icon className="h-4 w-4 shrink-0" />
                                {tab.label}
                                {needs2fa && tab.key === "security" ? (
                                    <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200">
                                        Required
                                    </span>
                                ) : null}
                            </NavLink>
                        );
                    })}
                </nav>
            </div>
        </div>
    );
}
